import {useIntl} from 'react-intl';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {FetchDataParams, HeaderButton, ReactiveTable} from "react-ive-tables";
import useSWR, {mutate} from "swr";
import {
    Client,
    ClientInvoicesSummary,
    Invoice,
    InvoiceSubtotals,
    InvoiceWithPayment,
    Payment,
    SendInvoicesToClientRequest
} from "../../lib/types/types";
import {ComponentContainer} from "../../lib/template/ComponentContainer";
import {Payments} from "./Payments";
import {Button} from "primereact/button";
import cloneDeep from "lodash.clonedeep";
import {IDropdownOption} from "@xal3xfx/react-utils/dist/util-service";
import {Dropdown} from "primereact/dropdown";
import {Column, ColumnBodyOptions} from "primereact/column";
import moment from "moment";
import {useDialog} from "../../lib/hooks/useDialog";
import {useToast} from "../../lib/hooks/useToast";
import {InvoiceService} from "../../lib/services/invoice-service";
import {Calendar} from "primereact/calendar";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {FileUpload, FileUploadHandlerEvent, FileUploadSelectEvent} from "primereact/fileupload";
import {useLocation} from 'react-router-dom';
import {MultiSelect} from "primereact/multiselect";
import {Dialog} from "primereact/dialog";
import {MobileFilters} from "../../lib/template/MobileFilters";
import {MobileInvoiceFilters} from "./MobileInvoiceFilters";
import {LazyFetchingService} from "../../lib/services/lazy-fetching-service";
import {OverlayPanel} from "primereact/overlaypanel";
import {DynamicForm} from "@xal3xfx/react-utils";
import {formElements} from './send-invoices-to-client-form'
import {sendInvoicesToClientValidation} from './send-invoices-to-client-validation'
import {FilterMatchMode} from "primereact/api";
import {formatNumber, parseDateFilter, statusClassNameMap} from "../../lib/common";
import {
    DataTableRowEditCompleteEvent,
    DataTableRowEvent,
    DataTableRowToggleEvent,
    DataTableSelectionSingleChangeEvent,
    DataTableStateEvent
} from "primereact/datatable";
import {ExportExcelParams} from "react-ive-tables/dist/ReactiveTable";
import {InputNumber} from "primereact/inputnumber";
import {Files} from "../../lib/template/Files";
import {Divider} from "primereact/divider";
import PenaltyPaymentHistory from "./PenaltyPaymentHistory";
import PenaltyPaymentForInvoice from "./PenaltyPaymentForInvoice";
import styled from "styled-components";
import {InputText} from "primereact/inputtext";
import {Badge} from "primereact/badge";
import SelectedInvoices from './SelectedInvoices';
import {InvoiceIconsOverlay} from "./invoiceIcons/InvoiceIconsOverlay";
import {InvoiceIconOption, InvoiceIconsFilter} from "./invoiceIcons/InvoiceIconsFilter";
import {useUserInfo} from "../../lib/hooks/useUserInfo";

interface Props {
    previewOnly: boolean;
    clientId?: number;
    initialFilters?: Record<string, string>;
}

interface LocationState {
    invoiceNumber: string;
}

interface TableInitialFilters {
    invoiceNumber?: string;
    clientId?: number;
    statusId?: number;

}

//It is very important this service to stay out of the component so that it is created only once and preserves state.
const lazyFetchingService = new LazyFetchingService<InvoiceWithPayment, InvoiceSubtotals>('/invoices/lazy');

const SubTotalTable = styled.table`
    border: 1px solid black;
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 1rem
`

const SubTotalTableRow = styled.tr`
    border: 1px solid black;
`

const SubTotalTableData = styled.td`
    border: 1px solid black;
    text-align: left;
`

const SubTotalTableHead = styled.caption`
    border: 1px solid black;
    border-bottom: 0px;
    font-weight: bold;
    font-size: 1.25rem
`

const Invoices: React.FC<Props> = props => {
    const {formatMessage: f} = useIntl();
    const toastHelpers = useToast();
    const invoiceService = new InvoiceService(f, toastHelpers);
    const {showConfirmDialog} = useDialog();
    const location = useLocation();
    let requestFromLocation = location.state as LocationState | undefined;
    const sendToClientOverlayRef = useRef<any>(null);
    const invoiceIconsOverlayRef = useRef<OverlayPanel | null>(null);
    const {userInfo} = useUserInfo();

    const [invoices, setInvoices] = useState<InvoiceWithPayment[]>();
    const [expandedRows, setExpandedRows] = useState<any>([]);
    const [showImportInvoiceDialog, setShowImportInvoiceDialog] = useState(false);
    const [invoiceImportType, setInvoiceImportType] = useState<'Nexus' | 'Nsys' | 'RedLori' | 'RedLoriHtml' | 'MicroInvest'>();
    const [fileToUpload, setFileToUpload] = useState<File>();
    const [initialFilters, setInitialFilters] = useState({});
    const [mobileFilters, setMobileFilters] = useState<any>();
    const [isMobile, setIsMobile] = useState(false);
    const [mobileFiltersDialogShown, setMobileFiltersDialogShown] = useState(false);
    const [paymentAdded, setPaymentAdded] = useState<number | undefined>();
    const [rebuildColumns, setRebuildColumns] = useState<number>();
    const [clientIdFilter, setClientIdFilter] = useState<number>();
    const [clientIdFilterForInvoiceNumber, setClientIdFilterForInvoiceNumber] = useState<number>();
    const [emailToSend, setEmailToSend] = useState<string>();
    const [filtersToSend, setFiltersToSend] = useState<any>();
    const [formElementsState, setFormElementsState] = useState(formElements);
    const [sortByClient, setSortByClient] = useState(false);
    const [sortAlphabetically, setSortAlphabetically] = useState(false);
    const [showPayFeesDialog, setShowPayFeesDialog] = useState(false);
    const [amountToPay, setAmountToPay] = useState(0);
    const [renderSaveButtons, setRenderSaveButtons] = useState(false);
    const [showFilesDialog, setShowFilesDialog] = useState(false);
    const [selectedInvoiceForFiles, setSelectedInvoiceForFiles] = useState<InvoiceWithPayment>();
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithPayment>();
    const [columnOrder, setColumnOrder] = useState<string[]>(['invoiceIcon', 'invoiceNumber', 'date', 'clientId', 'accountableEmployeeId', 'total', 'paidAmount', 'restAmount', 'penalty', 'dueDate', 'statusId', 'invoiceType', 'operations']);
    const [note, setNote] = useState<string>();
    const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
    const [selectedInvoiceIdForIconUpdate, setSelectedInvoiceIdForIconUpdate] = useState<number>();
    //Serialized invoiceIconsFilter as string - needed for exportingExcel because the <ReactiveTable> component serializes all filters to string when sending them to exportExcel and we get [Object object] ...
    const [invoiceIconsFilter, setInvoiceIconsFilter] = useState<string>();


    const sortConverter = (sortEvent?: DataTableStateEvent) => {
        let result: { order: Record<string, string> } = {order: {}};

        if (isMobile) {
            result.order['clientId'] = sortAlphabetically ? 'ascending' : 'descending';
            // return {order: {'clientId': 'ascending'}}
        }

        sortEvent?.multiSortMeta?.forEach(meta => {
            result.order[meta.field] = meta.order === -1 ? 'descending' : 'ascending';
        })

        // return {order: {'clientId': 'ascending'}}

        return result;
    }

    const getInitialFilters = () => {
        const initialFilters: any = props.initialFilters || {};

        if (props.clientId) {
            initialFilters.clientId = String(props.clientId);
        }

        return initialFilters;
    }

    const fetcher = lazyFetchingService.getLazySortableFetcher(sortConverter, getInitialFilters());

    const customFetchData = ({offset, limit, filters, sort}: FetchDataParams) => {

        //Transform invoiceIcon filter
        if (filters['invoiceIcon'] && filters['invoiceIcon'].value) {
            const transformedFilters = cloneDeep(filters);
            const invoiceIconFilter: InvoiceIconOption[] = transformedFilters['invoiceIcon'].value;
            transformedFilters['invoiceIcon'].value = invoiceIconFilter.map(el => el.icon + ' ' + el.color).join(',')
            return lazyFetchingService.getDataFetcher({offset, limit, filters: transformedFilters, sort});
        }
        return lazyFetchingService.getDataFetcher({offset, limit, filters, sort});
    }

    const {data: clientOptions} = useSWR<IDropdownOption[], Error>(!props.previewOnly ? '/clients/dropdown' : null);
    const {data: employeeOptions} = useSWR<IDropdownOption[], Error>(!props.previewOnly ? '/employees/dropdown' : null);
    const {data: clientFilterOptions} = useSWR<ClientInvoicesSummary[], Error>(!props.previewOnly ? '/clients/invoices-dropdown' : null);
    const {data: statusOptions} = useSWR<IDropdownOption[], Error>('/invoices/statuses');
    const {data: originalInvoices} = useSWR<Awaited<ReturnType<typeof fetcher>>, Error>(`/invoices/lazy`, {fetcher: fetcher});
    const {data: selectedClient} = useSWR<Client, Error>(clientIdFilter ? '/clients/' + clientIdFilter : null);
    const invoicesNumberOptionsMutationKey = (clientIdFilterForInvoiceNumber || props.clientId) ? `/invoices/invoiceNumberDropdown/${clientIdFilterForInvoiceNumber || props.clientId}` : '/invoices/invoiceNumberDropdown';
    const {data: invoiceNumberOptions} = useSWR<IDropdownOption[], Error>(invoicesNumberOptionsMutationKey);
    const {data: selectedClientForPreview} = useSWR<Client, Error>((props.previewOnly && props.clientId) ? '/clients/' + props.clientId : null);

    const invoiceTypeOptions: IDropdownOption[] = [
        {
            id: 1,
            key: 1,
            description: f({id: "invoices"})
        },
        {
            id: 2,
            key: 2,
            description: f({id: "penaltyInvoices"})
        },
        {
            id: 3,
            key: 3,
            description: f({id: "creditNotes"})
        },
        {
            id: 4,
            key: 4,
            description: f({id: "transactions"})
        },
        {
            id: 5,
            key: 5,
            description: f({id: "manualInvoices"})
        },
        {
            id: 6,
            key: 6,
            description: f({id: "advancePayments"})
        },
        {
            id: 7,
            key: 7,
            description: f({id: "debitNotes"})
        },
        {
            id: 8,
            key: 8,
            description: f({id: "viamonsTransactions"})
        },

    ]


    const showEmailButton = () => {
        // return (!props.previewOnly || selectedClient === undefined || originalInvoices === undefined || originalInvoices.totalRecords === 0)
        if (props.previewOnly)
            return false;
        if (selectedClient === undefined || originalInvoices === undefined || originalInvoices.totalRecords === 0)
            return false;
        return true;
    }

    const {data: clientEmailOptions} = useSWR<IDropdownOption[], Error>(!showEmailButton() ? null : '/clients/clientEmailsDropdown/' + clientIdFilter);


    useEffect(() => {
        return () => {
            lazyFetchingService.clearState();
            window.history.replaceState({}, document.title);
        }
    }, [])

    useEffect(() => {
        if (!selectedClientForPreview) return;

        let tempColumnOrder: string[] = [];
        if (props.previewOnly && userInfo && userInfo.roles) {
            tempColumnOrder = userInfo.roles[0] === 'admin' ? ['invoiceIcon', 'invoiceNumber', 'date', 'total', 'paidAmount', 'restAmount'] : ['invoiceNumber', 'date', 'total', 'paidAmount', 'restAmount'];
            if (selectedClientForPreview.penaltyPercent !== undefined && selectedClientForPreview.penaltyPercent > 0) tempColumnOrder.push('penalty');
            tempColumnOrder.push('dueDate', 'statusId', 'invoiceType', 'operations');
        }
        setColumnOrder(tempColumnOrder);
        setRebuildColumns(Date.now());

    }, [selectedClientForPreview])

    useEffect(() => {
        let width = (window.innerWidth > 0) ? window.innerWidth : window.screen.width;
        setIsMobile(width <= 960);
        const onResize = () => {
            width = (window.innerWidth > 0) ? window.innerWidth : window.screen.width;
            setIsMobile(width <= 960);
        }
        window.addEventListener("resize", (onResize));

        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (requestFromLocation && requestFromLocation.invoiceNumber)
            setInitialFilters((prevState) => {
                return {...prevState, invoiceNumber: [requestFromLocation?.invoiceNumber]}
            })
    }, [requestFromLocation])

    useEffect(() => {
        if (originalInvoices && originalInvoices.rows) {
            setInvoices(originalInvoices.rows.map(invoice => {
                return {
                    ...invoice, date: new Date(invoice.date), payments: invoice.payments.map(payment => {
                        return {...payment, paymentDate: new Date(payment.paymentDate)}
                    })
                }
            }));
            setRebuildColumns(Date.now());
        }
    }, [originalInvoices]);

    useEffect(() => {
        if (clientOptions && statusOptions && invoiceNumberOptions) {
            setRebuildColumns(Date.now())
        }
    }, [clientOptions, statusOptions, invoiceNumberOptions]);

    useEffect(() => {
        setRebuildColumns(Date.now());
    }, [initialFilters])

    useEffect(() => {
        if (!clientEmailOptions) return;
        const tempFormElements = {...formElementsState};
        tempFormElements.email.options = clientEmailOptions.map(el => {
            return {id: el.description, description: el.description, key: el.key}
        });

        setFormElementsState(tempFormElements);
    }, [clientEmailOptions])

    useEffect(() => {
        // if(isSelectedClientLoading) return;

        if (selectedClient) {
            setEmailToSend(selectedClient.email);
        } else {
            setEmailToSend('');
            setFiltersToSend(undefined)
        }
        // }, [selectedClient, isSelectedClientLoading]);
    }, [selectedClient]);

    const onRowExpand = (event: DataTableRowEvent) => {
    }

    const onRowCollapse = (event: DataTableRowEvent) => {
    }

    const onRowToggle = (event: DataTableRowToggleEvent) => {
        setExpandedRows(event.data);
    }

    const rowExpansionTemplate = (data: InvoiceWithPayment) => {
        // const penalties = originalInvoices?.subtotals?.penaltiesAmount
        const penalties = data.penalty;
        return <Payments previewOnly={(penalties && penalties > 0)  || data.invoiceType === InvoiceType.AdvancePayment|| props.previewOnly} data={data.payments}
                         paymentAdded={paymentAdded}/>
    }

    const addPayment = (rowData: InvoiceWithPayment) => {
        setInvoices(prevInvoices => {
            if (!prevInvoices) return prevInvoices;
            setExpandedRows((prevData: any) => {
                return {...prevData, [rowData.id]: true}
            });

            const emptyPayment: Payment = {
                id: 0,
                amount: rowData.restAmount,
                paymentDate: new Date(),
                invoiceId: rowData.id
            }
            const tempInvoices = cloneDeep(prevInvoices);
            const currentInvoice = tempInvoices!.find(el => el.id === rowData.id);
            if (currentInvoice) {
                currentInvoice.payments.push(emptyPayment);
            }
            return tempInvoices
        });

        setPaymentAdded(new Date().getTime());
    }


    const clientFilterTemplate = (option: ClientInvoicesSummary) => {
        if (option) {
            const hasUnpaidInvoices = option.hasUnpaidInvoices;
            const label = option.clientName;
            const restAmount = option.invoicesRestAmount;
            const hasOverpaidInvoices = !hasUnpaidInvoices && restAmount < 0;
            return (
                <div
                    className={hasOverpaidInvoices ? "overpaid-invoice" : !hasUnpaidInvoices ? 'paid-invoice' : ""}>{`${label} ${formatNumber(restAmount)}`}</div>
            );
        }
        return <span>{"Asdf"}</span>;
    };

    const getSpecialFilters = () => {
        return {
            clientId: (options: any) => <Dropdown filter={true} showClear value={options.value}
                                                  resetFilterOnHide
                                                  filterInputAutoFocus={true}
                                                  options={clientFilterOptions || []}
                                                  optionValue={'clientId'} optionLabel={'clientName'}
                                                  placeholder={f({id: 'chooseLabel'})}
                                                  itemTemplate={clientFilterTemplate}
                                                  onChange={(e) => options.filterApplyCallback(e.value)}
                                                  style={{textAlign: "left"}}/>,
            statusId: (options: any) => <MultiSelect filter={true} showClear value={options.value}
                                                     resetFilterOnHide
                                                     options={statusOptions || []}
                                                     optionValue={'id'} optionLabel={'description'}
                                                     placeholder={f({id: 'chooseLabel'})}
                                                     onChange={(e) => options.filterApplyCallback(e.value)}
                                                     selectedItemTemplate={selectedStatusTemplate}
                                                     itemTemplate={statusItemTemplate}
                                                     style={{textAlign: "left"}}/>,
            invoiceNumber: (options: any) => <MultiSelect filter={true} showClear value={options.value}
                                                          maxSelectedLabels={2}
                                                          resetFilterOnHide
                                                          virtualScrollerOptions={{itemSize: 30}}
                                                          options={invoiceNumberOptions || []}
                                                          optionValue={'description'} optionLabel={'description'}
                                                          placeholder={f({id: 'chooseLabel'})}
                                                          onChange={(e) => options.filterApplyCallback(e.value)}
                                                          style={{textAlign: "left"}}/>,
            invoiceType: (options: any) => <MultiSelect filter={true} showClear value={options.value}
                                                        maxSelectedLabels={2}
                                                        resetFilterOnHide
                                                        virtualScrollerOptions={{itemSize: 30}}
                                                        options={invoiceTypeOptions || []}
                                                        optionValue={'id'} optionLabel={'description'}
                                                        placeholder={f({id: 'chooseLabel'})}
                                                        onChange={(e) => options.filterApplyCallback(e.value)}
                                                        style={{textAlign: "left"}}/>,
            invoiceIcon: (options: any) => <InvoiceIconsFilter options={options}/>,
            accountableEmployeeId: (options: any) => <MultiSelect filter={true} showClear value={options.value}
                                                                  maxSelectedLabels={2}
                                                                  resetFilterOnHide
                                                                  virtualScrollerOptions={{itemSize: 30}}
                                                                  options={employeeOptions || []}
                                                                  optionValue={'id'}
                                                                  optionLabel={'description'}
                                                                  placeholder={f({id: 'chooseLabel'})}
                                                                  onChange={(e) => options.filterApplyCallback(e.value)}
                                                                  style={{textAlign: "left"}}/>,

        }
    }

    const getSpecialEditors = () => {
        return {
            date: (options: any) => <Calendar value={options.value} onChange={(e) => options.editorCallback(e.value)}/>,
            clientId: (options: any) => <Dropdown value={options.value} options={clientOptions}
                                                  onChange={(e) => options.editorCallback(e.value)} optionValue={'id'}
                                                  optionLabel={'description'}/>
        }
    }



    const statusItemTemplate = (option: any) => {
        const className = statusClassNameMap[option.description];
        return <div key={option.key} style={{display: 'flex', justifyContent: 'center'}}><span
            style={{display: 'flex', justifyContent: 'center'}}
            className={className}>{f({id: option.description})}</span></div>;
    };

    const generateStatusColumnTemplate = (rowData: InvoiceWithPayment) => {
        const statusName = statusOptions?.find(status => status.id === rowData.statusId)?.description || "";
        const className = statusClassNameMap[statusName];
        return <div key={rowData.id} style={{display: 'flex', justifyContent: 'center'}}><span
            style={{display: 'flex', justifyContent: 'center'}}
            className={className}>{f({id: statusName})}</span></div>;
    }

    const rowClass = (data: InvoiceWithPayment) => {
        return {
            'overdue-invoice': data.statusId < 3 && new Date() > new Date(data.dueDate)
        }
    }

    const selectedStatusTemplate = (option: any) => {
        var statusOption = statusOptions!.find(el => el.id === option)
        if (statusOption)
            return <div key={statusOption.key}>
                {f({id: statusOption.description})}
            </div>
        return "Изберете"
    }

    const handleShowFilesDialog = (invoice: InvoiceWithPayment) => {
        setShowFilesDialog(true);
        setSelectedInvoiceForFiles(invoice);
    }

    const getPenaltyTemplate = (rowData: InvoiceWithPayment) => {
        if (rowData.penalty === 0)
            return formatNumber(rowData.penalty);
        else
            return <Button label={formatNumber(rowData.penalty).toString()} onClick={() => {
                setSelectedInvoice(rowData);
                // setShowInvoicePenaltiesDialog(true);
            }}/>

    }

    const getColumnTemplate = () => {
        return {
            operations: (rowData: InvoiceWithPayment, columnOptions: ColumnBodyOptions) => {

                const isDisabled = () => {
                    //Enable editing and deleting imported credit notices but do not allow for penalty invoices
                    if (rowData.invoiceType == InvoiceType.CreditNotice && !rowData.invoiceNumber.startsWith('99'))
                        return false;

                    return rowData.invoiceType !== InvoiceType.Invoice;
                }

                const isDeleteDisabled = () => {
                    //Enable editing and deleting imported credit notices but do not allow for penalty invoices
                    if (rowData.invoiceType == InvoiceType.CreditNotice && !rowData.invoiceNumber.startsWith('99') && !rowData.invoiceNumber.startsWith('41'))
                        return false;


                    if(rowData.invoiceType === InvoiceType.Invoice) {
                        return rowData.payments.some(payment => payment.bankTransactionId)
                    }
                    return true;
                }

                if (props.previewOnly) return <Button icon={'pi pi-file'} className={'mr-2 p-button-info'}
                                                      tooltip={f({id: 'files'})}
                                                      tooltipOptions={{position: 'top'}}
                                                      onClick={() => handleShowFilesDialog(rowData)}>{rowData.filesCount > 0 &&
                    <Badge severity={'warning'} style={{position: 'absolute', right: 0, top: 0}}
                           value={rowData.filesCount}/>}</Button>

                const editButton = columnOptions.rowEditor && !columnOptions.rowEditor.editing ?
                    <Button icon={'pi pi-pencil'} tooltip={f({id: 'edit'})} tooltipOptions={{position: 'top'}}
                            onClick={columnOptions.rowEditor.onInitClick}
                            disabled={isDisabled()}/> : null;
                const saveButton = columnOptions.rowEditor && columnOptions.rowEditor.editing ?
                    <Button icon={'pi pi-check'} tooltip={f({id: 'save'})} tooltipOptions={{position: 'top'}}
                            onClick={columnOptions.rowEditor.onSaveClick}/> : null;
                const cancelButton = columnOptions.rowEditor && columnOptions.rowEditor.editing ?
                    <Button className={'ml-2'} icon={'pi pi-times'} tooltip={f({id: 'cancel'})}
                            tooltipOptions={{position: 'top'}} onClick={columnOptions.rowEditor.onCancelClick}/> : null;
                return <div className={'flex justify-content-center gap-1'}>
                    <Button icon={'pi pi-plus'} className={'mr-2 p-button-success'} tooltip={f({id: 'addPayment'})}
                            disabled={rowData.penalty > 0}
                            tooltipOptions={{position: 'top'}} onClick={() => addPayment(rowData)}/>
                    <Button icon={'pi pi-file'} className={'mr-2 p-button-info'} tooltip={f({id: 'files'})}
                            tooltipOptions={{position: 'top'}}
                            onClick={() => handleShowFilesDialog(rowData)}>
                        {rowData.filesCount > 0 &&
                            <Badge severity={'warning'} style={{position: 'absolute', right: 0, top: 0}}
                                   value={rowData.filesCount}/>}
                    </Button>

                    {editButton}
                    {saveButton}
                    {cancelButton}

                    <Button icon={'pi pi-trash'} className={'p-button-danger ml-2'} tooltip={f({id: 'delete'})}
                            tooltipOptions={{position: 'top'}} onClick={() => handleDelete(rowData)}
                            disabled={isDeleteDisabled()}/>
                </div>
            },
            clientId: (rowData: InvoiceWithPayment) => clientOptions?.find(client => client.id === rowData.clientId)?.description || f({id: 'error'}),
            date: (rowData: InvoiceWithPayment) => moment(rowData.date).format("DD-MM-YYYY"),
            total: (rowData: InvoiceWithPayment) => formatNumber(rowData.total),
            paidAmount: (rowData: InvoiceWithPayment) => formatNumber(rowData.paidAmount),
            restAmount: (rowData: InvoiceWithPayment) => formatNumber(rowData.restAmount),
            penalty: getPenaltyTemplate,
            statusId: generateStatusColumnTemplate,
            dueDate: (rowData: InvoiceWithPayment) => moment(rowData.dueDate).format("DD-MM-YYYY"),
            invoiceType: (rowData: InvoiceWithPayment) => {
                let invoiceTypeLabel = "";
                let severity: "success" | "info" | "help" | "danger" | "secondary" | "warning" = "info";
                let tooltip = "";

                switch (rowData.invoiceType) {
                    case InvoiceType.Invoice: {
                        invoiceTypeLabel = "Ф";
                        severity = "info";
                        tooltip = f({id: 'invoice'});
                        break;
                    }
                    case InvoiceType.PenaltyInvoice: {
                        invoiceTypeLabel = "ФН";
                        severity = "help";
                        tooltip = f({id: 'penaltyInvoice'});
                        break;
                    }
                    case InvoiceType.CreditNotice: {
                        invoiceTypeLabel = "КИ";
                        severity = "danger";
                        tooltip = f({id: 'creditNote'});
                        break;
                    }
                    case InvoiceType.TransactionsInvoice: {
                        invoiceTypeLabel = "Т";
                        severity = "success";
                        tooltip = f({id: 'transactions'});
                        break;
                    }
                    case InvoiceType.ManualInvoice: {
                        invoiceTypeLabel = "РФ";
                        severity = "success";
                        tooltip = f({id: 'manualInvoice'});
                        break;
                    }
                    case InvoiceType.AdvancePayment: {
                        invoiceTypeLabel = "АП";
                        severity = "success";
                        tooltip = f({id: 'advancePayment'});
                        break;
                    }
                    case InvoiceType.DebitNotice: {
                        invoiceTypeLabel = "ДИ";
                        severity = "secondary";
                        tooltip = f({id: 'debitNotice'});
                        break;
                    }
                    case InvoiceType.ViamonsTransactionInvoice: {
                        invoiceTypeLabel = "ВМ";
                        severity = "success";
                        tooltip = f({id: 'viamonsTransactionInvoice'});
                        break;
                    }
                }

                return <>
                    <Button
                        tooltip={tooltip}
                        tooltipOptions={{position: "top"}}
                        label={invoiceTypeLabel}
                        severity={severity}
                    />
                </>


            },
            invoiceIcon: (rowData: InvoiceWithPayment) => {
                return <Button
                    text
                    size="large"
                    icon={`pi ${rowData.invoiceIcon ? rowData.invoiceIcon.split(' ')[0] : 'pi-ellipsis-h'}`}
                    className={`${rowData.invoiceIcon ? rowData.invoiceIcon.split(' ')[1] : 'text-color'} shadow-none`}
                    onClick={e => {
                        if (props.previewOnly) return;

                        setSelectedInvoiceIdForIconUpdate(rowData.id);
                        setTimeout(() => {
                            invoiceIconsOverlayRef.current?.toggle(e);
                        }, 1);
                    }}
                />
            },
            accountableEmployeeId: (rowData: InvoiceWithPayment) => employeeOptions?.find(el => rowData.accountableEmployeeId === el.id)?.description,
            invoiceNumber: (rowData: InvoiceWithPayment) => {
                if (rowData.payments && rowData.payments.length > 0 && (rowData.statusId === 3 || rowData.statusId === 4)){
                    const lastPayment = rowData.payments
                        .slice()
                        .sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime())[0];

                    const lastPaymentDate = new Date(lastPayment.paymentDate);
                    const dueDate = new Date(rowData.dueDate);

                    if (lastPaymentDate > dueDate) {
                        return <div className={'flex flex-column'}>
                            <label>{rowData.invoiceNumber}</label>
                            <span className={'text-red-300'}>{f({id: 'paidLate'})}</span>
                        </div>;
                    }
                }

                return rowData.invoiceNumber;
            }

        }
    }

    const handleUpdate = async (data: Invoice) => {
        const updatedPayment = await invoiceService.update(data);
        if (updatedPayment !== null) {
            await mutate('/invoices/lazy')
            await mutate('/clients/invoices-dropdown');
        }
    }

    const handleDelete = async (invoice: Invoice) => {
        if (invoice.id !== undefined) {
            showConfirmDialog({body: f({id: 'confirmDeleteInvoice'}, {invoiceNumber: invoice.invoiceNumber})}).then((confirmed) => {
                if (!confirmed) return;
                invoiceService.delete(invoice.id!).then(async res => {
                    if (res) {
                        await mutate('/invoices/lazy');
                        await mutate('/clients/invoices-dropdown');
                    }
                })
            })
        }
    }

    const handleRowEdit = async (event: DataTableRowEditCompleteEvent) => {
        const newValues: Invoice = event.newData as Invoice;
        //Update
        if (newValues.id !== 0) {
            await handleUpdate(newValues);
        }
    }

    const rightHeaderButtons = [
        {
            onClick: () => {
                setInvoiceImportType('MicroInvest');
                setShowImportInvoiceDialog(true);
            },
            icon: 'pi pi-file-import',
            className: 'mr-2',
            label: f({id: 'importMicroInvest'}),
        },
        {
            onClick: () => {
                setInvoiceImportType('RedLoriHtml');
                setShowImportInvoiceDialog(true);
            },
            icon: 'pi pi-file-import',
            className: 'mr-2',
            label: f({id: 'importRedLoriHtml'}),
        },
        {
            onClick: () => {
                setInvoiceImportType('RedLori');
                setShowImportInvoiceDialog(true);
            },
            icon: 'pi pi-file-import',
            className: 'mr-2',
            label: f({id: 'importRedLori'}),
        },
        {
            onClick: () => {
                setInvoiceImportType('Nexus');
                setShowImportInvoiceDialog(true);
            },
            icon: 'pi pi-file-import',
            className: 'mr-2',
            label: f({id: 'importNexus'}),
        },
        {
            onClick: () => {
                setInvoiceImportType('Nsys');
                setShowImportInvoiceDialog(true);
            },
            icon: 'pi pi-file-import',
            className: '',
            label: f({id: 'importNsys'}),
        }
    ]

    const leftHeaderButtons: HeaderButton[] = []

    if (showEmailButton()) leftHeaderButtons.push({
        onClick: (e: any) => sendToClientOverlayRef.current.toggle(e),
        icon: 'pi pi-paperclip',
        className: 'p-button-info',
        label: f({id: 'sendToClient'})
    })

    if (isMobile && !props.previewOnly) leftHeaderButtons.push({
        onClick: () => {
            setSortAlphabetically(!sortAlphabetically)
        },
        icon: sortAlphabetically ? 'fa fa-arrow-up-a-z' : 'fa fa-arrow-down-a-z',
        className: 'p-button-info',
        label: ""
    });

    useEffect(() => {
        mutate("/invoices/lazy");
    }, [sortAlphabetically])

    const handleInvoicesError = (err: any) => {
        if (err.response && err.response.status === 409) {
            const skippedInvoices = err.response.data.skippedInvoices?.length || 0;
            const unmatchedInvoices = err.response.data.unmatchedInvoices?.length || 0;
            const message = <div>
                {(err.response.data.skippedInvoices && err.response.data.skippedInvoices.length > 0) ? <>
                    <h3>{f({id: 'skippedInvoices'})}</h3>
                    <ul>
                        {err.response.data.skippedInvoices.map((invoiceNumber: string) => <li
                            key={invoiceNumber}>{invoiceNumber}</li>)}
                    </ul>
                </> : null}

                {(err.response.data.unmatchedInvoices && err.response.data.unmatchedInvoices.length > 0) ? <>
                    <h3>{f({id: 'unmatchedInvoices'})}</h3>
                    <ul>
                        {err.response.data.unmatchedInvoices.map((invoiceNumber: string) => <li
                            key={invoiceNumber}>{invoiceNumber}</li>)}
                    </ul>
                </> : null}

                {(err.response.data.totalInvoicesCount > 0) ? <>
                    <h3>{f({id: "uploadedTotalFrom"}, {
                        uploaded: err.response.data.totalInvoicesCount - (skippedInvoices + unmatchedInvoices),
                        totalInvoices: err.response.data.totalInvoicesCount
                    })}</h3>
                </> : null}
            </div>
            toastHelpers.showToast("warn", f({id: "invoicesPartiallyUploaded"}), message, true);
            setShowImportInvoiceDialog(false);
            mutate("/invoices/lazy");
            mutate('/clients/invoices-dropdown');
        } else {
            toastHelpers.showToast("error", f({id: "operationUnsuccessful"}), f({id: 'internalServerError'}), true);
        }
    }

    const handleUpload = (e: FileUploadHandlerEvent) => {
        if (fileToUpload && !props.previewOnly) {
            if (invoiceImportType === 'Nsys') {
                invoiceService.uploadNsysInvoice(fileToUpload).then((res) => {
                    toastHelpers.showToast(res ? "success" : "warn", f({id: res ? "operationSuccessful" : "operationUnsuccessful"}), f({id: res ? "invoicesUploaded" : "invoicesNotUploaded"}), true);
                    mutate("/invoices/lazy");
                    mutate('/clients/invoices-dropdown');
                }).catch(err => {
                    //Handle invoice conflict
                    handleInvoicesError(err);
                });
            } else if (invoiceImportType === 'Nexus') {
                invoiceService.uploadNexusInvoice(fileToUpload).then((res) => {
                    toastHelpers.showToast(res ? "success" : "warn", f({id: res ? "operationSuccessful" : "operationUnsuccessful"}), f({id: res ? "invoicesUploaded" : "invoicesNotUploaded"}), true);
                    mutate("/invoices/lazy");
                    mutate('/clients/invoices-dropdown');
                }).catch(err => {
                    //Handle invoice conflict
                    handleInvoicesError(err);
                });

            } else if (invoiceImportType === 'RedLori') {
                invoiceService.uploadRedLoriInvoice(fileToUpload).then((res) => {
                    toastHelpers.showToast(res ? "success" : "warn", f({id: res ? "operationSuccessful" : "operationUnsuccessful"}), f({id: res ? "invoicesUploaded" : "invoicesNotUploaded"}), true);
                    mutate("/invoices/lazy");
                    mutate('/clients/invoices-dropdown');
                }).catch(err => {
                    //Handle invoice conflict
                    handleInvoicesError(err);
                });

            } else if (invoiceImportType === 'RedLoriHtml') {
                invoiceService.uploadRedLoriHtmlInvoice(fileToUpload).then((res) => {
                    toastHelpers.showToast(res ? "success" : "warn", f({id: res ? "operationSuccessful" : "operationUnsuccessful"}), f({id: res ? "invoicesUploaded" : "invoicesNotUploaded"}), true);
                    mutate("/invoices/lazy");
                    mutate('/clients/invoices-dropdown');
                }).catch(err => {
                    //Handle invoice conflict
                    handleInvoicesError(err);
                });

            } else if (invoiceImportType === 'MicroInvest') {
                invoiceService.uploadMicroInvest(fileToUpload).then((res) => {
                    toastHelpers.showToast(res ? "success" : "warn", f({id: res ? "operationSuccessful" : "operationUnsuccessful"}), f({id: res ? "invoicesUploaded" : "invoicesNotUploaded"}), true);
                    mutate("/invoices/lazy");
                    mutate('/clients/invoices-dropdown');
                }).catch(err => {
                    //Handle invoice conflict
                    handleInvoicesError(err);
                });

            } else {
                console.error("Unrecognized invoice import type: " + invoiceImportType);
            }
        }
    }

    const handleFileSelect = (e: FileUploadSelectEvent) => {
        if (e.files[0])
            setFileToUpload(e.files[0]);
    }

    const handleMobileFilters = (filters: MobileFilters) => {
        setInitialFilters({...initialFilters, ...filters});
        setMobileFilters(filters);
        setMobileFiltersDialogShown(false)
    }

    const getMobileSubTotals = () => {
        const totalFormatted = formatNumber(originalInvoices?.subtotals?.total);
        const paidAmountFormatted = formatNumber(originalInvoices?.subtotals?.paidAmount);
        const restAmountFormatted = formatNumber(originalInvoices?.subtotals?.restAmount);
        const penaltiesAmountFormatted = formatNumber(originalInvoices?.subtotals?.penaltiesAmount);

        // return <div className={"flex flex-column"}>
        //     <FlexContainer>
        //         <p>{totalFormatted}</p>
        //     </FlexContainer>
        //
        //     <FlexContainer>
        //         <p>{paidAmountFormatted}</p>
        //     </FlexContainer>
        //
        //     <FlexContainer>
        //         <p>{restAmountFormatted}</p>
        //     </FlexContainer>
        //
        //     <FlexContainer>
        //         <p>{penaltiesAmountFormatted}</p>
        //     </FlexContainer>
        //
        //     <FlexContainer>
        //         <p>{hasPenalties}</p>
        //     </FlexContainer>
        // </div>
        return <SubTotalTable>
            <SubTotalTableHead>{f({id: "total"})}</SubTotalTableHead>
            <SubTotalTableRow>
                <SubTotalTableData>{f({id: "vatIncluded"})}</SubTotalTableData>
                <SubTotalTableData>{totalFormatted}</SubTotalTableData>
            </SubTotalTableRow>
            <SubTotalTableRow>
                <SubTotalTableData>{f({id: "paidAmount"})}</SubTotalTableData>
                <SubTotalTableData>{paidAmountFormatted}</SubTotalTableData>
            </SubTotalTableRow>
            <SubTotalTableRow>
                <SubTotalTableData>{f({id: "restAmount"})}</SubTotalTableData>
                <SubTotalTableData>{restAmountFormatted}</SubTotalTableData>
            </SubTotalTableRow>
            <SubTotalTableRow>
                <SubTotalTableData>{f({id: "penalty"})}</SubTotalTableData>
                <SubTotalTableData>{penaltiesAmountFormatted}</SubTotalTableData>
            </SubTotalTableRow>
        </SubTotalTable>
    }

    const getFooterGroup = () => {
        const totalFormatted = formatNumber(originalInvoices?.subtotals?.total);
        const paidAmountFormatted = formatNumber(originalInvoices?.subtotals?.paidAmount);
        const restAmountFormatted = formatNumber(originalInvoices?.subtotals?.restAmount);
        const penaltiesAmountFormatted = formatNumber(originalInvoices?.subtotals?.penaltiesAmount);
        const hasPenalties = originalInvoices?.subtotals?.penaltiesAmount && originalInvoices?.subtotals?.penaltiesAmount > 0;

        return <ColumnGroup>
            <Row>
                <Column colSpan={props.previewOnly ? 3 : 6} footer={f({id: "total"}) + ":"}
                        footerStyle={{textAlign: 'center'}}/>
                <Column footer={totalFormatted}
                        footerStyle={{textAlign: 'center', fontWeight: 'bold'}}/>
                <Column
                    footer={paidAmountFormatted}
                    footerStyle={{textAlign: 'center', fontWeight: 'bold'}}/>
                <Column
                    footer={restAmountFormatted}
                    footerStyle={{textAlign: 'center', fontWeight: 'bold'}}/>
                <Column
                    footer={clientIdFilterForInvoiceNumber ?
                        <Button label={penaltiesAmountFormatted.toString()}
                                onClick={() => setShowPayFeesDialog(true)}/> : penaltiesAmountFormatted.toString()}
                    footerStyle={{textAlign: 'center', fontWeight: 'bold'}}/>
                <Column colSpan={4}/>
            </Row>
        </ColumnGroup>;
    }

    const handleOnFilter = (filteredInvoices: InvoiceWithPayment[], filters: any) => {
        if (clientIdFilterForInvoiceNumber !== undefined && filters['clientId'] !== undefined && filters['clientId'].value !== clientIdFilterForInvoiceNumber) {
            setInitialFilters((prevState: any) => {
                if (prevState.invoiceNumber === undefined) {
                    return {...prevState, invoiceNumber: []}
                } else {
                    return {...prevState, invoiceNumber: undefined}
                }
            })
        }

        if (filters['invoiceIcon'] && filters['invoiceIcon'].value && Array.isArray(filters['invoiceIcon'].value) && filters['invoiceIcon'].value.length) {
            const invoiceIconFilter = filters['invoiceIcon'].value;
            setInvoiceIconsFilter(invoiceIconFilter.map((el: any) => el.icon + ' ' + el.color).join(','));
        } else {
            setInvoiceIconsFilter(undefined);
        }

        //Handle invoiceNumber filter
        if (filters['invoiceNumber'] !== undefined && filters['invoiceNumber'].value.length > 0) {
            setSelectedInvoices(filters['invoiceNumber'].value);
        } else {
            setSelectedInvoices([]);
        }

        if (filters['clientId'] !== undefined && filters['clientId'].value !== '' && filters['clientId'].value !== null) {
            setClientIdFilterForInvoiceNumber(filters['clientId'].value);
        } else {
            setClientIdFilterForInvoiceNumber(undefined);
        }
        //Status should be selected
        const filteredByPaidStatus = filters['statusId'] !== undefined && filters['statusId'].value !== undefined && filters['statusId'].value !== null && Array.isArray(filters['statusId'].value) && filters['statusId'].value.includes(3);
        //Only available for paid and partially paid invoices.
        const filteredByStatus = filters['statusId'] !== undefined && filters['statusId'].value !== undefined && filters['statusId'].value !== null && Array.isArray(filters['statusId'].value) && filters['statusId'].value.length > 0;
        if (filters['clientId'] !== undefined && filters['clientId'].value !== '' && filters['clientId'].value !== null && !filteredByPaidStatus && filteredByStatus) {
            setClientIdFilter(filters['clientId'].value);
            const parsedFilters = Object.keys(filters).reduce((acc: any, key: string) => {
                //Handle multiselect with empty array.
                if (Array.isArray(filters[key].value) && filters[key].value.length === 0) return acc;

                if (filters[key].value !== null && filters[key].value !== '' && filters[key].value !== undefined)
                    return {...acc, [key]: String(filters[key].value)}
                return acc
            }, {});
            setFiltersToSend(parsedFilters);
        } else {
            setClientIdFilter(undefined);
            setFiltersToSend(undefined);
        }
    }

    const handleSelection = (event: DataTableSelectionSingleChangeEvent<any>) => {
        const rowData: InvoiceWithPayment = event.value as InvoiceWithPayment;
        setExpandedRows((prevData: any) => {
            if (prevData[rowData.id]) {
                const res = Object.keys(prevData).filter(el => +el !== rowData.id).reduce((acc, el) => {
                    //@ts-ignore
                    acc[el] = true;
                    return acc;
                }, {});
                return res;
            }
            return {...prevData, [rowData.id]: true}
        });
    }

    const handleSendInvoicesToClient = (values: { email: (string[]) }) => {
        if (clientIdFilter === undefined || values.email === undefined || filtersToSend.clientId === undefined || +filtersToSend.clientId !== +clientIdFilter) return Promise.resolve(false);
        const data: SendInvoicesToClientRequest = {
            clientId: clientIdFilter,
            filters: filtersToSend,
            email: values.email
        }

        return new Promise<boolean>((resolve, reject) => {
            resolve(true);
            sendToClientOverlayRef.current.hide();
            invoiceService.sendInvoicesToClient(data).then(invoicesSent => {
                if (invoicesSent) {
                    mutate("/clients/" + clientIdFilter);
                    resolve(true);
                    toastHelpers.showToast("info", f({id: 'operationSuccessful'}), f({id: "invoicesSentToClient"}));
                } else {
                    resolve(false);
                    toastHelpers.showToast("warn", f({id: 'operationUnSuccessful'}), f({id: "invoicesNotSentToClient"}));
                }
            }).catch(err => {
                resolve(false);
                toastHelpers.showToast("error", f({id: 'operationUnSuccessful'}), f({id: "internalServerError"}));
            });
        })
    }

    const exportExcel = (params: ExportExcelParams) => {

        //Reverse date filter
        if (params.filters && params.filters['date']) {
            params.filters['date'] = parseDateFilter(params.filters['date']);
        }

        if (props.clientId) {
            params.filters.clientId = String(props.clientId);
        }

        if (props.initialFilters) {
            params.filters = {...params.filters, ...props.initialFilters};
        }


        const filters = Object.keys(params.filters).reduce((acc: any, key: keyof typeof params.filters) => {
            if (params.filters[key]) {
                return {...acc, [key]: params.filters[key]}
            }
            return acc;
        }, {});

        if (invoiceIconsFilter) {
            filters['invoiceIcon'] = invoiceIconsFilter;
        }

        const parsedSortOrder = params.sort?.reduce((acc: any, el: any) => {
            return {...acc, [el.field]: 'ascending'}
        }, {})
        document.body.style.cursor = "wait";
        //Only order is needed (sort param is sent but not needed)
        invoiceService.exportExcel({...params, filters, order: parsedSortOrder}).then(res => {
            import('file-saver').then(FileSaver => {
                const blob = new Blob([res.data], {type: res.headers["content-type"]});
                FileSaver.default.saveAs(blob, params.excelName + ".xlsx");
            });
        }).finally(() => document.body.style.cursor = "auto")
    }

    const handlePayPenalties = () => {
        if (clientIdFilterForInvoiceNumber === undefined || amountToPay <= 0) return;
        const amount = amountToPay > originalInvoices?.subtotals?.penaltiesAmount! ? originalInvoices?.subtotals?.penaltiesAmount! : amountToPay;
        invoiceService.payPenalties(clientIdFilterForInvoiceNumber, amount, selectedInvoices, note)
            .then(res => {
                if (res) {
                    toastHelpers.showToast("success", f({id: 'operationSuccessful'}), `${f({id: 'invoice'})} ${res}`, false);
                    mutate("/invoices/lazy");
                    mutate(invoicesNumberOptionsMutationKey);
                    setRebuildColumns(Date.now());
                    hidePenaltiesDialog();
                } else toastHelpers.showToast("error", f({id: 'operationUnsuccessful'}), f({id: "penaltiesPaidFail"}));

            })
    }

    const hidePenaltiesDialog = () => {
        setAmountToPay(0);
        setShowPayFeesDialog(false);
        setRenderSaveButtons(false);
    }

    const preparePenalties = () => {
        if (clientIdFilterForInvoiceNumber === undefined || amountToPay <= 0) return;

        const amount = amountToPay > originalInvoices?.subtotals?.penaltiesAmount! ? originalInvoices?.subtotals?.penaltiesAmount! : amountToPay;
        invoiceService.preparePenaltiesPayment(clientIdFilterForInvoiceNumber, amount, selectedInvoices).then(res => {
            setRenderSaveButtons(true);
            import('file-saver').then(FileSaver => {
                const blob = new Blob([res.data], {type: res.headers["content-type"]});
                FileSaver.default.saveAs(blob, "Фактури" + ".xlsx");
            });
        })
    }


    const closeInvoicePenaltyDialog = async () => {
        setSelectedInvoice(undefined);
        await mutate("/invoices/lazy");
        await mutate(invoicesNumberOptionsMutationKey);
        setRebuildColumns(Date.now());
    }


    const handleInvoiceIconOverlayHide = () => {
        setSelectedInvoiceIdForIconUpdate(undefined);
    };

    const handleInvoiceIconUpdated = () => {
        mutate("/invoices/lazy");
        handleInvoiceIconOverlayHide();
    };

    return <>
        <ComponentContainer componentName={f({id: 'invoices'})}>
            {isMobile && getMobileSubTotals()}


            {isMobile && <Button icon={'pi pi-filter'} label={f({id: 'filters'})}
                                 onClick={() => setMobileFiltersDialogShown(true)}/>}


            <ReactiveTable
                swr
                data={(!props.previewOnly || !clientOptions || !invoiceNumberOptions) && statusOptions ? invoices : undefined}
                totalRecords={originalInvoices?.totalRecords}
                fetchData={customFetchData}
                columnOrder={userInfo.roles?.includes('client') ? columnOrder.filter(col => col !== 'accountableEmployeeId') : columnOrder}
                ignoreFilters={['operations', 'paidAmount', 'restAmount', 'penalty', 'dueDate']}
                sortableColumns={['clientId', 'date']}
                columnTemplate={getColumnTemplate()}
                specialFilters={getSpecialFilters()}
                rowEditHandler={props.previewOnly ? undefined : handleRowEdit}
                specialEditors={props.previewOnly ? undefined : getSpecialEditors()}
                onFilterCb={handleOnFilter}
                rebuildColumns={rebuildColumns}
                exportExcel={exportExcel}
                xlsx={`${moment().format("MM-DD HH-mm")}_${f({id: 'invoices'})}`}
                initialFilters={initialFilters}
                filtersMatchMode={{statusId: FilterMatchMode.EQUALS, invoiceNumber: FilterMatchMode.IN}}
                editableColumns={['clientId', 'total']}
                showHeader={true}
                rightHeaderButtons={props.previewOnly ? undefined : rightHeaderButtons}
                headerButtons={leftHeaderButtons}
                specialLabels={{date: 'invoiceDate', total: 'vatIncluded', statusId: 'status', invoiceIcon: 'no-text'}}
                expandable
                frozenColumns={!isMobile ? ['operations'] : undefined}
                paginatorOptions={isMobile ? [5, 10, 20] : [20, 30, 50]}
                // filtersMatchMode={{statusId: FilterMatchMode.IN , clientId: FilterMatchMode.EQUALS}}
                dtProps={{
                    removableSort: true,
                    className: 'p-datatable-no-select-outline invoices-table',
                    expandedRows,
                    onRowExpand,
                    onRowCollapse,
                    rowExpansionTemplate,
                    onRowToggle,
                    scrollable: true,
                    scrollHeight: "100vh",
                    footerColumnGroup: getFooterGroup(),
                    rowClassName: rowClass,
                    selectionMode: 'single',
                    dataKey: 'id',
                    onSelectionChange: handleSelection
                }}/>
        </ComponentContainer>
        {(showImportInvoiceDialog && invoiceImportType) &&
            <Dialog breakpoints={{'960px': '75vw', '640px': '100vw'}} style={{width: '50vw'}}
                    header={f({id: 'import' + invoiceImportType})} onHide={() => setShowImportInvoiceDialog(false)}
                    visible={showImportInvoiceDialog}>
                <FileUpload chooseLabel={f({id: "choose"})} mode="advanced" customUpload={true}
                            uploadHandler={handleUpload} onSelect={handleFileSelect}
                            accept={invoiceImportType === "RedLori" ? ".csv,.txt" : ".xlsx,.xls"}
                            maxFileSize={1000000}/>
            </Dialog>}

        {
            (mobileFiltersDialogShown && statusOptions && (props.previewOnly ? !clientFilterOptions : clientFilterOptions)) ?
                <Dialog header={f({id: 'filters'})} position={"top"} onHide={() => setMobileFiltersDialogShown(false)}
                        visible={mobileFiltersDialogShown} breakpoints={{'960px': '75vw', '640px': '100vw'}}
                        style={{width: '50vw'}}>
                    <MobileInvoiceFilters onFilterApply={handleMobileFilters} clientOptions={clientFilterOptions || []}
                                          statusOptions={statusOptions} filters={mobileFilters}
                                          isClientOptionsDropdown={false}
                                          statusClassNameMap={statusClassNameMap}
                                          showClientFilter={!props.previewOnly}/>
                </Dialog> : null
        }

        {(showPayFeesDialog && clientIdFilterForInvoiceNumber) &&
            <Dialog breakpoints={{'960px': '75vw', '640px': '100vw'}}
                    header={f({id: "payPenalties"}, {companyName: clientOptions?.find(el => el.id === clientIdFilterForInvoiceNumber)?.description})}
                    onHide={hidePenaltiesDialog}
                    visible={showPayFeesDialog}>
                <SelectedInvoices invoice={originalInvoices} selectedInvoices={selectedInvoices}/>
                <div className={"grid p-fluid justify-content-center mt-5"}>
                    <span className="p-float-label col-12 md:col-6">

                        <InputNumber
                            disabled={renderSaveButtons}
                            value={amountToPay}
                            max={originalInvoices?.subtotals?.penaltiesAmount}
                            mode={"currency"}
                            maxFractionDigits={10}
                            currency={"bgn"}
                            onChange={e => {
                                if (originalInvoices?.subtotals?.penaltiesAmount && originalInvoices?.subtotals?.penaltiesAmount > 0) {
                                    setAmountToPay(e.value || 0)
                                }
                            }}/>
                        <label>{f({id: "amount"})}</label>
                    </span>

                    <span className="p-float-label col-12 md:col-6">
                        <InputText
                            disabled={renderSaveButtons}
                            value={note}
                            onChange={e => setNote(e.target.value)}/>
                        <label className={"ml-2"}>{f({id: "note"})}</label>
                    </span>
                </div>


                <div style={{alignItems: "center"}} className={"grid flex justify-content-end mt-3"}>
                    <div className={'flex align-itmes-center'}>
                        <label>{f({id: "preview"})}:</label>
                        <Button className={"col-12 md:col-5 ml-2"} disabled={amountToPay <= 0}
                                icon={"pi pi-file-excel"}
                                severity={"success"} size={"large"} outlined onClick={preparePenalties}/>
                    </div>

                </div>

                {renderSaveButtons &&
                    <div className={"grid justify-content-even mt-3"}>
                        <Button className={"col-12 md:col-5"} label={f({id: "save"})} onClick={handlePayPenalties}/>
                        <Button className={"col-12 md:col-5"} label={f({id: "cancel"})}
                                onClick={hidePenaltiesDialog}/>
                    </div>
                }

                <Divider align={"center"}>
                    <h2>{f({id: "penaltyPaymentsHistory"})}</h2>
                </Divider>

                <PenaltyPaymentHistory clientId={clientIdFilterForInvoiceNumber} originalInvoices={originalInvoices}/>

            </Dialog>
        }

        <OverlayPanel ref={sendToClientOverlayRef} showCloseIcon style={{minWidth: '300px'}}>
            <h2>{f({id: 'lastSentInvoicesMail'}, {dateAndTime: selectedClient?.lastSentInvoicesMail ? moment(selectedClient?.lastSentInvoicesMail).format("DD-MM-YYYY HH:mm") : f({id: 'never'})})}</h2>
            <DynamicForm initialValues={{email: emailToSend ? [emailToSend] : []}} formElements={formElements}
                         onCancelUpdate={() => 0}
                         rowClassName={'col-12'}
                         validationSchema={sendInvoicesToClientValidation}
                         formButtonsClassName={'col-12'}
                         saveButtonLabel={f({id: 'send'})}
                         isUpdate={false} onUpdate={() => Promise.resolve(false)}
                         fieldOrder={['email']}
                         onCreate={handleSendInvoicesToClient}>
            </DynamicForm>
        </OverlayPanel>

        {
            selectedInvoiceIdForIconUpdate && (
                <OverlayPanel ref={invoiceIconsOverlayRef} showCloseIcon onHide={handleInvoiceIconOverlayHide}>
                    <InvoiceIconsOverlay invoiceId={selectedInvoiceIdForIconUpdate} onSave={handleInvoiceIconUpdated}/>
                </OverlayPanel>
            )
        }

        {
            (showFilesDialog && selectedInvoiceForFiles) ?
                <Dialog header={f({id: 'filesForInvoice'}, {invoiceNumber: selectedInvoiceForFiles?.invoiceNumber})}
                        onHide={() => setShowFilesDialog(false)}
                        visible={showFilesDialog} breakpoints={{'960px': '75vw', '640px': '100vw'}}
                        style={{width: '50vw'}}>
                    <Files selectedRecordId={selectedInvoiceForFiles.id} allowEdit={!props.previewOnly}
                           showDownloadTime={true}
                           parentMutationKey={"/invoices/lazy"}
                           dataKey={"/invoices/files/all/"} downloadHandler={invoiceService.downloadInvoiceFile}
                           uploadHandler={invoiceService.uploadInvoiceFile}
                           deleteHandler={invoiceService.deleteInvoiceFile}
                    />
                </Dialog> : null
        }

        {selectedInvoice &&
            <PenaltyPaymentForInvoice selectedInvoice={selectedInvoice} closeDialog={closeInvoicePenaltyDialog}
                                      previewOnly={props.previewOnly}/>}


    </>
};

export enum InvoiceType {
    Invoice = 1,
    PenaltyInvoice = 2,
    CreditNotice = 3,
    TransactionsInvoice = 4,
    ManualInvoice = 5,
    AdvancePayment = 6,
    DebitNotice= 7,
    ViamonsTransactionInvoice = 8,
}

export default Invoices;
